import { createRouter, createWebHashHistory } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useUserStore } from '../stores/user'
import { pathAllowedForDelivery, firstDeliveryHome } from '../utils/menuCatalog'
import { isStaffAppShell, requestNativeWorkbench, requestNativeDelivery } from '../utils/nav'

const WEB_APP_ONLY_PATHS = ['/express', '/pick', '/delivery', '/load', '/label-templates']

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { public: true }
  },
  {
    path: '/shop-features',
    name: 'ShopFeatures',
    component: () => import('../views/ShopFeatures.vue'),
    meta: { title: '店铺功能' }
  },
  {
    path: '/setup-guide',
    name: 'SetupGuide',
    component: () => import('../views/SetupGuide.vue'),
    meta: { title: '建站引导' }
  },
  {
    path: '/user',
    name: 'UserHome',
    component: () => import('../views/UserHome.vue'),
    meta: { title: '我的' }
  },
  {
    path: '/delivery',
    name: 'Delivery',
    component: () => import('../views/Delivery.vue'),
    meta: { title: '配送' }
  },
  {
    path: '/express',
    name: 'ExpressPickup',
    component: () => import('../views/ExpressPickup.vue'),
    meta: { title: '取快递' }
  },
  {
    path: '/customer-map',
    name: 'CustomerMap',
    component: () => import('../views/CustomerMap.vue'),
    meta: { title: '客户卫星地图' }
  },
  {
    path: '/pick',
    name: 'Pick',
    component: () => import('../views/Pick.vue'),
    meta: { title: '分检打标' }
  },
  {
    path: '/load',
    name: 'Load',
    component: () => import('../views/Load.vue'),
    meta: { title: '装车' }
  },
  {
    path: '/label-templates',
    name: 'LabelTemplates',
    component: () => import('../views/LabelTemplates.vue'),
    meta: { title: '标签排版' }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('../views/Layout.vue'),
    redirect: '/portal',
    children: [
      {
        path: 'portal',
        name: 'Portal',
        component: () => import('../views/Portal.vue'),
        meta: { title: '工作台' }
      },
      {
        path: 'tenants',
        name: 'Tenants',
        component: () => import('../views/Tenants.vue'),
        meta: { title: '租户管理', platformOnly: true }
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/Dashboard.vue'),
        meta: { title: '统计' }
      },
      {
        path: 'rank',
        redirect: '/users'
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('../views/Users.vue'),
        meta: { title: '客户管理' }
      },
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('../views/Orders.vue'),
        meta: { title: '订单管理' }
      },
      {
        path: 'commission-category',
        name: 'CommissionCategory',
        component: () => import('../views/CommissionCategory.vue'),
        meta: { title: '分类' }
      },
      {
        path: 'points',
        name: 'Points',
        component: () => import('../views/Points.vue'),
        meta: { title: '积分管理' }
      },
      {
        path: 'regions',
        name: 'Regions',
        component: () => import('../views/Regions.vue'),
        meta: { title: '区域管理' }
      },
      {
        path: 'banners',
        name: 'Banners',
        component: () => import('../views/Banners.vue'),
        meta: { title: '轮播图管理', platformOnly: true }
      },
      {
        path: 'station-poster',
        name: 'StationPoster',
        component: () => import('../views/StationPoster.vue'),
        meta: { title: '自提点海报' }
      },
      {
        path: 'stations',
        name: 'Stations',
        component: () => import('../views/Stations.vue'),
        meta: { title: '网点管理' }
      },
      {
        path: 'station-rebind',
        name: 'StationRebind',
        component: () => import('../views/StationRebind.vue'),
        meta: { title: '新客户改绑自提点', adminOnly: true }
      },
      {
        path: 'withdrawals',
        name: 'Withdrawals',
        component: () => import('../views/Withdrawals.vue'),
        meta: { title: '提现记录' }
      },
      {
        path: 'logs',
        name: 'Logs',
        component: () => import('../views/Logs.vue'),
        meta: { title: '操作日志' }
      },
      {
        path: 'admins',
        name: 'Admins',
        component: () => import('../views/Admins.vue'),
        meta: { title: '管理员' }
      },
      {
        path: 'config',
        name: 'Config',
        component: () => import('../views/Config.vue'),
        meta: { title: '系统配置', platformOnly: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes
})

/** 发版后旧缓存仍请求已删除的 js 分片时，自动强刷一次，避免页面一直转圈/卡住 */
const CHUNK_RELOAD_KEY = 'jf_chunk_reload_v1'
router.onError((err) => {
  const msg = String(err?.message || err || '')
  const isChunkFail =
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  if (!isChunkFail) return
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1') {
      ElMessage.error('页面资源已更新，请完全关闭 App 后重新打开')
      return
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  } catch (_) {
    /* ignore */
  }
  // 用 replace 清掉可能残留的查询串，避免相对路径错乱
  const base = `${window.location.origin}${window.location.pathname || '/'}`
  const hash = window.location.hash || '#/'
  window.location.replace(`${base}${hash}`)
})

router.afterEach(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch (_) {
    /* ignore */
  }
})

router.beforeEach((to, from, next) => {
  const userStore = useUserStore()

  // 阶段1：App WebView 内不再进入 H5 圆形工作台，统一回原生方形工作台
  if (to.path === '/portal' && isStaffAppShell()) {
    requestNativeWorkbench()
    next(false)
    return
  }

  // App 内今日配送走原生页，不走 H5 /delivery
  if (to.path === '/delivery' && isStaffAppShell()) {
    requestNativeDelivery()
    next(false)
    return
  }

  if (
    !isStaffAppShell() &&
    WEB_APP_ONLY_PATHS.includes(to.path) &&
    userStore.token &&
    userStore.isStaff &&
    !userStore.isPlatformAdmin
  ) {
    ElMessage.info('取快递、分检、配送请在手机 App 中使用')
    next('/portal')
    return
  }

  if (!to.meta.public && !userStore.token) {
    next('/login')
  } else if (to.path === '/login' && userStore.token) {
    next(userStore.homePath || '/dashboard')
  } else if (to.meta.platformOnly && !userStore.isPlatformAdmin) {
    next(userStore.homePath || '/dashboard')
  } else if (
    userStore.isPlatformAdmin &&
    (['/portal', '/delivery', '/pick', '/label-templates', '/express', '/user', '/shop-features', '/customer-map'].includes(to.path) ||
      to.path === '/' ||
      to.path === '/dashboard')
  ) {
    next('/tenants')
  } else if ((userStore.isTenantAdmin || userStore.isDelivery) && to.path === '/user') {
    // 店长/配送员勿进客户页
    next(userStore.homePath || '/portal')
  } else if (userStore.isDelivery) {
    const blocked = ['/admins', '/shop-features', '/config', '/tenants', '/banners', '/setup-guide']
    const ok = pathAllowedForDelivery(to.path, userStore.deliveryMenus, to.query || {})
    if (blocked.includes(to.path) || !ok) {
      const home = firstDeliveryHome(userStore.deliveryMenus)
      if (from.path === home && to.path !== home) {
        ElMessage.warning('当前账号没有该功能权限')
        next(false)
        return
      }
      // 已在目标首页：不要 next(home) 造成「点了没反应」
      if (to.path === home) {
        next(false)
        return
      }
      next(home)
      return
    }
    next()
  } else {
    next()
  }
})

export default router
